import { useRef, useState } from 'react';
import { ApiCallError, api, previewUrlFor, type ItemPatch, type RoundItem } from '../lib/api';
import { exactTime, isoDate, timeAgo } from '../lib/timeAgo';
import FakePage from '../play/FakePage';
import TourLink from '../tour/TourLink';
import type { FakeItem } from '../data/types';

/* One headline in the builder.

   Edits save on blur rather than on every keystroke: an LLM-free autosave is still a write per
   character otherwise, and PocketBase is on the other end of a tunnel. Nothing is lost — blur
   fires on tab-out, click-away and form submit.

   For a FAKE headline this is also the page composer: images, supporting copy, ribbon and
   footer, with a live preview rendered by the SAME component the recording surface uses, so
   the preview cannot drift from what Adorian will see. */

/* What a field is called on screen, so a conflict says "Headline" rather than "headline" or,
   worse, "footerNote". */
/* Must stay in step with isPlaceholderImage in functions/_lib/roundsApi.js. Two copies because
   one runs on the edge and one in the browser; if they ever disagree, the server's is the one
   that decides what reaches a camera. */
const isPlaceholderName = (name: string) => /^(awaiting|placeholder|temp)[-_]/i.test(name || '');

const FIELD_LABELS: Record<string, string> = {
  headline: 'Headline', sourceName: 'Source', sourceUrl: 'Link', publishedAt: 'Published',
  body: 'Supporting copy', footerNote: 'Footer', notes: 'Notes', verdict: 'Real or fake',
  artStatus: 'Artwork', imageHint: 'Image hint',
};

interface Props {
  item: RoundItem;
  index: number;
  total: number;
  onChanged: (item: RoundItem) => void;
  onDeleted: (id: string) => void;
  /* Absent while the list is filtered. Both reorder gestures are positional, and with rows
     hidden the neighbour you would swap with is not the one on screen — so the controls go away
     rather than silently doing the wrong thing. */
  onMove?: (id: string, direction: -1 | 1) => void;
  /** Dropped onto this row: insert the dragged item BEFORE this index in the current order. */
  onDropBefore?: (dragId: string, insertBefore: number) => void;
  /** Id of the row currently being dragged, so every card can show where it would land. */
  dragging?: string | null;
  onDragState?: (id: string | null) => void;
  /** Round-level look, mirrored into the preview so it matches the recording surface exactly. */
  ribbonLabel?: string;
  headlineSize?: string;
  showWordmark?: boolean;
  roundId: string;
  busy?: boolean;
  /* Selection for the bulk move. Undefined = the card renders no checkbox at all, so nothing
     changes for callers that do not offer moving. */
  selected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
}

export default function ItemCard({
  item, index, total, onChanged, onDeleted, onMove,
  onDropBefore, dragging, onDragState,
  ribbonLabel = '', headlineSize, showWordmark = true, roundId, busy,
  selected, onSelect,
}: Props) {
  /* Which edge the row would drop against, so the gap opens where it will actually land.
     Without it you are dragging blind and only find out after you let go. */
  const [dropEdge, setDropEdge] = useState<'above' | 'below' | null>(null);
  const isDragging = dragging === item.id;
  const [draft, setDraft] = useState(item);
  /* The row's arrival date, formatted once. Empty for anything written before the field
     existed, which renders as an em dash rather than a misleading date. */
  const arrivedAt = isoDate(item.createdAt);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullNote, setPullNote] = useState<string | null>(null);
  /* Whether the link field was interacted with since it was last saved — typed in, pasted
     into, anything. The value alone cannot tell a deliberate re-paste from a tab-through, since
     both leave the same text in the box, so intent is tracked separately. */
  const [linkTouched, setLinkTouched] = useState(false);
  /* A save that was refused because someone else got there first. Holds THEIR version and the
     patch that lost, so nothing typed is thrown away while the person decides. */
  const [conflict, setConflict] = useState<{ theirs: RoundItem; by: string; patch: ItemPatch } | null>(null);
  /* Open by default on a fake headline. This panel is the ONLY place images can be uploaded,
     and behind a collapsed button it read as a missing feature rather than a hidden one. */
  const [open, setOpen] = useState(item.verdict === 'fake');
  const fileInput = useRef<HTMLInputElement>(null);

  // Keep the local draft in step when the parent replaces the item (reorder, upload, refresh).
  if (item.id !== draft.id || item.updated !== draft.updated) {
    if (!saving && !uploading && !conflict) setDraft(item);
  }

  async function persist(patch: ItemPatch, force = false) {
    setSaving(true);
    setError(null);
    try {
      /* The version this edit started from. Sending it is what turns "last save wins" into
         "second save is told". `force` omits it — that is the Keep Mine path, taken only after
         the person has seen the other version. */
      const saved = await api.updateItem(item.id, patch, force ? undefined : item.updated);
      setConflict(null);
      onChanged(saved);
    } catch (err) {
      if (err instanceof ApiCallError && err.status === 409) {
        /* Deliberately NOT reverting the draft. The whole failure this fixes is losing what
           someone typed, and dropping it here to show the conflict would do exactly that. */
        const details = err.details as { item?: RoundItem; editedBy?: string } | null;
        if (details?.item) {
          setConflict({ theirs: details.item, by: details.editedBy || 'Someone else', patch });
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not save.');
      setDraft(item); // put the field back to the last known-good value
    } finally {
      setSaving(false);
    }
  }

  /* Only write when the value actually changed — blur fires on every tab-out, and an
     unchanged PATCH would still stamp last_edited_by and add an activity row, filling the
     history with edits nobody made. */
  const saveIfChanged = (key: 'headline' | 'sourceName' | 'sourceUrl' | 'publishedAt' | 'body' | 'footerNote' | 'notes', value: string) => {
    if (item[key] === value) return;
    void persist({ [key]: value } as ItemPatch);
  };

  /* Pasting a link replaces the headline with the article's own, every time — that is the
     point: paste, get the real headline, no retyping. Re-pasting the SAME link does it again,
     which is how you undo an edit and get the original wording back.

     What must not trigger it is merely tabbing through the field, which would silently clobber
     a headline someone deliberately reworded. The two are indistinguishable by value — same
     text in the box either way — so the trigger is whether the field was TYPED IN, not whether
     the text changed.

     If the site refuses us — plenty of outlets block datacenter IPs — the existing headline is
     LEFT ALONE and a quiet note explains why. Wiping someone's typing because a fetch 403'd
     would be much worse than not helping at all. */
  async function applyLink(rawUrl: string, touched: boolean) {
    const url = rawUrl.trim();
    setLinkTouched(false);
    // Untouched and unchanged: they just passed through. Leave everything alone.
    if (!touched && url === item.sourceUrl) return;
    setPullNote(null);

    if (!url) { await persist({ sourceUrl: '' }); return; }
    // Cleared and retyped to the same thing, with nothing to re-pull? Still re-pull: that is
    // exactly the "put the original headline back" gesture.

    setPulling(true);
    try {
      const found = await api.linkTitle(url);
      const patch: ItemPatch = { sourceUrl: found.url || url };
      /* Only when the field is blank, and deliberately UNLIKE the headline, which a re-paste is
         meant to overwrite. A date is either right or wrong — there is no better wording of it
         to go back to — so if someone has corrected one by hand, the article does not get to
         argue. Plenty of outlets stamp a "filed" date onto a story they later rewrote. */
      if (found.publishedAt && !item.publishedAt.trim()) patch.publishedAt = found.publishedAt;
      if (found.ok && found.title) {
        patch.headline = found.title;
        // Only fill the outlet when it is blank — never overwrite what someone chose to call it.
        if (!item.sourceName.trim() && found.sourceName) patch.sourceName = found.sourceName;
      } else if (found.reason) {
        setPullNote(`${found.reason} The link is saved.`);
      }
      const updated = await api.updateItem(item.id, patch);
      setDraft(updated);
      onChanged(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that link.');
      setDraft(item);
    } finally {
      setPulling(false);
    }
  }

  async function setVerdict(verdict: 'real' | 'fake') {
    if (verdict === item.verdict) return;
    setDraft({ ...draft, verdict });
    await persist({ verdict });
  }

  async function addImages(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    setError(null);
    try {
      onChanged(await api.uploadItemImages(item.id, Array.from(files)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeImage(name: string) {
    setUploading(true);
    try {
      onChanged(await api.uploadItemImages(item.id, [], [name]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that image.');
    } finally {
      setUploading(false);
    }
  }

  /* Only when the box holds something a browser could actually go to. A button that opens a
     blank tab is worse than no button, because it reads as the link being broken.

     ⚠️ new URL() alone is not the check. It is deliberately forgiving: `https:/broken` parses
     happily as `https://broken/`, so a half-typed link passed and offered a dead button. The
     pattern demands the `//` and a dotted host before the parse gets a say. */
  const openableUrl = (() => {
    const raw = draft.sourceUrl.trim();
    if (!/^https?:\/\/[^\s/]+\.[^\s/]/i.test(raw)) return '';
    try {
      const u = new URL(raw);
      return u.protocol === 'http:' || u.protocol === 'https:' ? raw : '';
    } catch {
      return '';
    }
  })();

  const isFake = draft.verdict === 'fake';
  const notReady = !draft.headline.trim()
    ? 'This headline has no text.'
    : !isFake && !draft.sourceUrl.trim()
      ? 'A real headline needs a source link — REVEAL has nowhere to go.'
      : isFake && draft.images.length === 0 && !draft.body.trim()
        ? 'This fake page has no image and no text.'
        : null;

  /* The editor asks PocketBase for a SMALL variant. It was loading the full original for a
     70px thumbnail — 3MB each on a real upload — so the strip and the preview sat blank while
     megabytes downloaded, which reads as "the upload didn't work".

     480x0 specifically, everywhere in the editor. Two PocketBase behaviours force that:
       1. Only sizes DECLARED on the field are generated. An undeclared size returns the full
          original with a 200 — it looks like it worked and silently ships 3MB.
       2. PB does not backfill thumbs for files uploaded before a size was added. 160x160 was
          added to the field config and still returns the original for existing images, and
          always will. Using it would quietly regress every image uploaded so far.
     480x0 has existed since the first upload: 276KB instead of 3MB, an 11x saving that
     applies to old and new images alike. The recording surface still uses the original. */
  /* The AWAITING IMAGE stand-in is deliberately NOT in the preview, because the preview says
     "exactly what plays on camera" and the play payload leaves stand-ins out. Showing it here
     would make that label a lie in the one place it has to be true — and the failure it protects
     against is a grey placeholder going out under the FABRICATED banner, which has happened.
     The stand-in is still visible as a thumbnail in the images row, which is where it belongs:
     something to look at and replace, not something to broadcast. */
  const previewImages = draft.images.filter((i) => !isPlaceholderName(i.name));
  const previewItem: FakeItem = {
    id: draft.id,
    verdict: 'fake',
    headline: draft.headline,
    images: previewImages.map((i) => `${i.url}?thumb=480x0`),
    body: draft.body,
    footerNote: draft.footerNote,
  };
  const standingIn = draft.images.length !== previewImages.length;

  return (
    <div
      /* The jump target for the readiness banner's headline numbers. Derived from the item id
         rather than the position, so a link still lands on the right headline after a reorder. */
      id={`headline-${item.id}`}
      className={`item-card ${isFake ? 'is-fake' : 'is-real'}${busy ? ' busy' : ''}`
        + `${isDragging ? ' is-dragging' : ''}${dropEdge ? ` drop-${dropEdge}` : ''}`}
      onDragOver={(e) => {
        if (!dragging || dragging === item.id) return;
        e.preventDefault(); // without this the drop is refused
        const box = e.currentTarget.getBoundingClientRect();
        setDropEdge(e.clientY < box.top + box.height / 2 ? 'above' : 'below');
      }}
      onDragLeave={() => setDropEdge(null)}
      onDrop={(e) => {
        e.preventDefault();
        const dragId = e.dataTransfer.getData('text/plain') || dragging;
        setDropEdge(null);
        onDragState?.(null);
        if (!dragId || dragId === item.id) return;
        /* Report the INSERTION POINT in the current order — before this row, or before the next
           one. The editor knows where the dragged row came from and adjusts for it leaving the
           list; doing that arithmetic here would need `from`, which this card does not have. */
        onDropBefore?.(dragId, dropEdge === 'below' ? index + 1 : index);
      }}
    >
      <div className="item-top">
        {/* ⚠️ ONE grid cell, not two. `.item-top` is a three-column grid; adding the checkbox
            as a fourth child pushed `.item-side` onto an implicit second row, where REAL/FAKE
            and Open Live rendered outside the card entirely. The checkbox stays OUTSIDE the
            draggable block — a checkbox inside a draggable element is awkward to tick, because
            pointer-down starts a drag instead of ticking. */}
        <div className="item-lead">
          {onSelect && (
            <input
              type="checkbox"
              className="item-pick"
              checked={Boolean(selected)}
              aria-label={`Select "${item.headline || 'untitled headline'}"`}
              onChange={(e) => onSelect(item.id, e.target.checked)}
            />
          )}
          <div
            className="item-num"
            draggable={Boolean(onDropBefore)}
            title={onDropBefore ? 'Drag to reorder' : 'Clear the filter to reorder'}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', item.id);
              e.dataTransfer.effectAllowed = 'move';
              onDragState?.(item.id);
            }}
            onDragEnd={() => { onDragState?.(null); setDropEdge(null); }}
          >
            <span className="grip" aria-hidden="true">⠿</span>
            {String(index + 1).padStart(2, '0')}
          </div>
        </div>

        <div className="item-main">
          <textarea
            className="item-headline"
            value={draft.headline}
            rows={2}
            placeholder="The headline as it appears on screen"
            onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
            onBlur={(e) => saveIfChanged('headline', e.target.value)}
          />

          {!isFake ? (
            <div className="item-real-fields">
              <label className="field">
                <span className="lab">Source</span>
                <input
                  type="text"
                  value={draft.sourceName}
                  placeholder="CNBC"
                  onChange={(e) => setDraft({ ...draft, sourceName: e.target.value })}
                  onBlur={(e) => saveIfChanged('sourceName', e.target.value)}
                />
              </label>
              {/* ⚠️ A <div>, not a <label>, and that is required rather than stylistic. A <label>
                  forwards its clicks to the control it wraps, so the Open link inside one would
                  focus the input instead of opening the article — the exact thing being added.
                  The input keeps an aria-label so it is still named. */}
              <div className="field">
                <span className="lab">
                  {pulling
                    ? 'Link · reading the headline…'
                    : 'Link (opens on REVEAL) · re-paste to restore the original headline'}
                </span>
                <div className="link-row">
                <input
                  type="text"
                  aria-label="Source link"
                  value={draft.sourceUrl}
                  placeholder="https://…"
                  disabled={pulling}
                  /* onChange alone is not enough: React suppresses it when a field is set to
                     the value it already held, so selecting all and pasting the SAME link fires
                     nothing. onPaste and onKeyDown fire on the gesture regardless of whether the
                     text ends up different, which is exactly the signal wanted here — "you did
                     something to this field", not "the string changed". */
                  onPaste={() => setLinkTouched(true)}
                  onKeyDown={() => setLinkTouched(true)}
                  onChange={(e) => { setDraft({ ...draft, sourceUrl: e.target.value }); setLinkTouched(true); }}
                  onBlur={(e) => void applyLink(e.target.value, linkTouched)}
                />
                {/* One click to the article. Checking every link by hand was select-all, copy,
                    new tab, paste — for every headline in a batch of two hundred.

                    Points at the DRAFT, not the saved value, so a link just pasted can be
                    checked before deciding to keep it. Blur fires applyLink first, which is
                    right: the link is saved and its headline pulled, and then the tab opens.

                    Always a new tab. Navigating away from the editor would abandon whatever
                    else is half-typed on the card. */}
                {openableUrl && (
                  <a
                    className="btn ghost link-open"
                    href={openableUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Open this article in a new tab"
                    data-testid="open-source"
                  >
                    Open ↗
                  </a>
                )}
                </div>
              </div>
              {/* ⚠️ A THIRD column, and `.item-real-fields` has an explicit grid-template. Adding
                  a child without widening the template drops it onto an implicit second row —
                  which is exactly how the selection checkbox broke this card's layout before. */}
              <label className="field">
                <span className="lab">Published</span>
                <input
                  type="text"
                  className="item-published"
                  value={draft.publishedAt}
                  placeholder="1998 or 1998-04-23"
                  title="A year, or a full date. Filled in automatically when the article says."
                  onChange={(e) => setDraft({ ...draft, publishedAt: e.target.value })}
                  onBlur={(e) => saveIfChanged('publishedAt', e.target.value.trim())}
                />
              </label>
              {/* The FOURTH column. Not an input: this is when the row landed in our database, so
                  there is nothing for anyone to type and editing it would be meaningless. Sits
                  next to Published because the two are constantly confused — that one is about
                  the article, this one is about us. */}
              <div className="field">
                <span className="lab">Arrived</span>
                <span
                  className="item-arrived"
                  title={arrivedAt ? `${exactTime(item.createdAt)} · ${timeAgo(item.createdAt)}` : 'Not recorded'}
                  data-testid="item-arrived"
                >
                  {arrivedAt || '—'}
                </span>
              </div>
            </div>
          ) : (
            <button className="btn ghost" onClick={() => setOpen((v) => !v)} style={{ alignSelf: 'flex-start' }}>
              {open ? 'Hide the Fake Page' : `Edit the Fake Page${draft.images.length ? ` · ${draft.images.length} image${draft.images.length === 1 ? '' : 's'}` : ' · no image yet'}`}
            </button>
          )}
        </div>

        <div className="item-side">
          <div className="verdict-switch">
            <button className={draft.verdict === 'real' ? 'on-real' : ''} onClick={() => setVerdict('real')}>REAL</button>
            <button className={draft.verdict === 'fake' ? 'on-fake' : ''} onClick={() => setVerdict('fake')}>FAKE</button>
          </div>
          <div className="item-actions">
            {/* Opens the page exactly as it plays, with whatever is saved right now. */}
            <a
              className="btn ghost"
              title="Open this page live, in a new tab"
              href={previewUrlFor(roundId, item.slug || item.id)}
              target="_blank"
              rel="noreferrer"
            >
              Open Live
            </a>
            {onMove && (
              <>
                <button className="btn ghost" title="Move up" disabled={index === 0} onClick={() => onMove(item.id, -1)}>↑</button>
                <button className="btn ghost" title="Move down" disabled={index === total - 1} onClick={() => onMove(item.id, 1)}>↓</button>
              </>
            )}
            <button className="btn ghost danger" title="Delete" onClick={() => onDeleted(item.id)}>✕</button>
          </div>
        </div>
      </div>

      {pullNote && <div className="item-warn">{pullNote}</div>}
      {notReady && <div className="item-warn">{notReady}</div>}
      {error && <div className="item-warn hard">{error}</div>}

      {/* A save that lost a race. Both versions are shown because the whole point is that
          neither is thrown away without someone looking at it. */}
      {conflict && (
        <div className="conflict">
          <strong>{conflict.by} saved this headline while you were editing it.</strong>
          <div className="conflict-versions">
            {(Object.keys(conflict.patch) as (keyof ItemPatch)[]).map((field) => (
              <div className="conflict-field" key={String(field)}>
                <span className="lab">{FIELD_LABELS[String(field)] || String(field)}</span>
                <div><span className="tag">Theirs</span> {String(conflict.theirs[field as keyof RoundItem] ?? '') || <em>empty</em>}</div>
                <div><span className="tag mine">Yours</span> {String(conflict.patch[field] ?? '') || <em>empty</em>}</div>
              </div>
            ))}
          </div>
          <div className="conflict-actions">
            <button className="btn primary" disabled={saving} onClick={() => void persist(conflict.patch, true)}>
              Keep mine
            </button>
            <button
              className="btn"
              disabled={saving}
              onClick={() => { setDraft(conflict.theirs); onChanged(conflict.theirs); setConflict(null); }}
            >
              Use theirs
            </button>
          </div>
        </div>
      )}
      {saving && <div className="item-saving">Saving…</div>}

      {isFake && open && (
        <div className="composer">
          <div className="composer-fields">
            {/* The worklist flag and the thing a designer needs to act on it. Fakes are the
                constraint on how many episodes can be made, and until now nothing recorded
                which ones were waiting or what they were waiting for. */}
            <div className="field art-state">
              <TourLink to="artwork" label="the artwork flag and the AWAITING IMAGE stand-in" />
              <label className="switch">
                <input
                  type="checkbox"
                  checked={draft.artStatus === 'needs-art'}
                  onChange={(e) => {
                    const artStatus = e.target.checked ? 'needs-art' : '';
                    setDraft({ ...draft, artStatus });
                    void persist({ artStatus });
                  }}
                />
                <span>Waiting on artwork</span>
              </label>
              <span className="hint-sm">
                {draft.artStatus === 'needs-art'
                  ? 'Counted on the Series list and held out of "ready to record". Clears itself when real artwork is uploaded.'
                  : 'Tick this while the graphic is still to be made.'}
              </span>
            </div>

            {draft.artStatus === 'needs-art' && (
              <label className="field">
                <span className="lab">Brief for whoever draws it</span>
                <textarea
                  rows={2}
                  value={draft.notes}
                  placeholder="What the picture should show. The headline alone is rarely enough."
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  onBlur={(e) => saveIfChanged('notes', e.target.value)}
                />
              </label>
            )}

            <div className="field">
              <span className="lab">Images under the headline</span>
              <div className="thumb-row">
                {draft.images.map((img) => (
                  <div className="thumb" key={img.name}>
                    <img src={`${img.url}?thumb=480x0`} alt="" loading="lazy" />
                    <button className="thumb-x" onClick={() => removeImage(img.name)} disabled={uploading} title="Remove">✕</button>
                  </div>
                ))}
                <button className="thumb add" onClick={() => fileInput.current?.click()} disabled={uploading || draft.images.length >= 6}>
                  {uploading ? '…' : '+'}
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/avif"
                  multiple
                  hidden
                  onChange={(e) => addImages(e.target.files)}
                />
              </div>
              <div className="hint-sm">
                These appear under the headline on the reveal. PNG, JPEG, WebP or AVIF, up to 6
                and 10MB each. Add one here and clear the supporting text below to let the photo
                carry the page. Upload a single finished design with all text empty and it
                shows full-screen instead.
              </div>
            </div>

            <label className="field">
              <span className="lab">Supporting text {draft.images.length > 0 ? '(optional — the image can carry it)' : ''}</span>
              <textarea
                rows={3}
                value={draft.body}
                placeholder="Optional line under the image."
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                onBlur={(e) => saveIfChanged('body', e.target.value)}
              />
            </label>

            <div className="composer-row">
              <label className="field">
                <span className="lab">Footer line</span>
                <input
                  type="text"
                  value={draft.footerNote}
                  placeholder="IN-HOUSE GRAPHIC"
                  onChange={(e) => setDraft({ ...draft, footerNote: e.target.value })}
                  onBlur={(e) => saveIfChanged('footerNote', e.target.value)}
                />
              </label>
              <div className="field">
                <span className="lab">Top left banner</span>
                <div className="hint-sm">
                  Set once for the whole series, at the top of this page.
                </div>
              </div>
            </div>
          </div>

          <div className="composer-preview">
            <span className="lab">Exactly what plays on camera</span>
            <FakePage item={previewItem} ribbonLabel={ribbonLabel} headlineSize={headlineSize} showWordmark={showWordmark} preview />
            {standingIn && (
              <p className="hint-sm">
                The AWAITING IMAGE stand-in is not shown here, because it never goes on camera.
                Upload the artwork and it replaces the stand-in automatically.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
